import { CheckOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { AutoComplete, Input, Select } from "antd";
import type { AutoCompleteProps, SelectProps } from "antd";
import React, { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";

const APP_SELECT_POPUP_CLASS = "app-select-popup";

const AppSelectPopupStyles = createGlobalStyle`
	.app-select-control .ant-select-arrow {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		top: 50%;
		margin-top: 0;
		transform: translateY(-50%);
	}

	.app-select-control .ant-select-arrow > span,
	.app-select-control .ant-select-arrow svg {
		display: block;
	}

	.${APP_SELECT_POPUP_CLASS} {
		padding: 0 !important;
		border-radius: 16px !important;
		box-shadow: 0 4px 12px rgba(21, 89, 165, 0.23) !important;
		overflow: visible;
	}

	.${APP_SELECT_POPUP_CLASS} .ant-select-item {
		min-height: 48px;
		padding: 13px 24px 13px 16px !important;
		border-radius: 0 !important;
		border-bottom: 1px solid #f0f0f0;
		background: #fff !important;
		color: #303030;
		font-size: 14px;
		transition: background-color 0.15s ease, color 0.15s ease;
	}

	.${APP_SELECT_POPUP_CLASS} .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
		background: #f3f4ff50 !important;
	}

	.${APP_SELECT_POPUP_CLASS} .ant-select-item:last-child {
		border-bottom: 0;
	}

	.${APP_SELECT_POPUP_CLASS} .ant-select-item-option-selected {
		color: #f72586 !important;
		font-weight: 400 !important;
	}

	.${APP_SELECT_POPUP_CLASS} .ant-select-item-option-state {
		display: none;
	}

	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-holder {
		border-radius: 16px;
		scrollbar-width: thin;
		scrollbar-color: #9b9b9b transparent;
	}

	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-holder::-webkit-scrollbar {
		width: 8px;
	}

	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-holder::-webkit-scrollbar-track {
		margin-block: 10px;
		background: transparent;
	}

	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-holder::-webkit-scrollbar-thumb {
		border: 2px solid transparent;
		border-radius: 8px;
		background: #9b9b9b;
		background-clip: padding-box;
	}

	/* Keep rc-virtual-list's full calculated track height. The options holder,
	   rather than this sibling scrollbar, owns the popup's rounded clipping. */
	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-scrollbar {
		right: 8px !important;
	}

	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-scrollbar-thumb {
		background: transparent !important;
	}

	.${APP_SELECT_POPUP_CLASS} .rc-virtual-list-scrollbar-thumb::before {
		position: absolute;
		inset: 4px 0;
		border-radius: 999px;
		background: #9b9b9b;
		content: "";
		pointer-events: none;
	}
`;

const CustomOption = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-height: 48px;
	padding: 13px 16px;
	border: 0;
	border-top: 1px solid #f0f0f0;
	background: #fff;
	color: #303030;
	font: 14px var(--font-family-app);
	cursor: pointer;
`;

const CustomPanel = styled.div`
	padding: 16px;
	background: #fff;
	font-family: var(--font-family-app);
`;

const CustomLabel = styled.div`
	margin-bottom: 4px;
	color: #707070;
	font-size: 12px;
`;

const CustomInput = styled(Input)`
	height: 40px;
	border-color: #cdd4f6;
	border-radius: 8px;
`;

const CustomActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 12px;
`;

const CustomAction = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 4px;
	border: 0;
	background: transparent;
	color: #4460ef;
	font: 600 12px var(--font-family-app);
	cursor: pointer;

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
`;

const keepCustomPanelOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
	event.preventDefault();
	event.stopPropagation();
};

export type AppSelectProps = SelectProps<string>;

export const AppSelect: React.FC<AppSelectProps> = ({ className, popupClassName, ...props }) => (
	<>
		<AppSelectPopupStyles />
		<Select<string>
			{...props}
			className={["app-select-control", className].filter(Boolean).join(" ")}
			popupClassName={[APP_SELECT_POPUP_CLASS, popupClassName].filter(Boolean).join(" ")}
		/>
	</>
);

export type AppAutoCompleteProps = AutoCompleteProps<string>;

export const AppAutoComplete: React.FC<AppAutoCompleteProps> = ({ className, popupClassName, ...props }) => (
	<>
		<AppSelectPopupStyles />
		<AutoComplete<string>
			{...props}
			className={["app-select-control", className].filter(Boolean).join(" ")}
			popupClassName={[APP_SELECT_POPUP_CLASS, popupClassName].filter(Boolean).join(" ")}
		/>
	</>
);

export interface CustomEntrySelectProps
	extends Omit<SelectProps<string>, "value" | "options" | "onChange" | "open" | "onOpenChange" | "dropdownRender"> {
	value?: string;
	options: Array<{ value: string; label: React.ReactNode }>;
	onChange: (value: string) => void;
	customOptionLabel?: React.ReactNode;
	customInputLabel?: React.ReactNode;
	customInputPlaceholder?: string;
}

export const CustomEntrySelect: React.FC<CustomEntrySelectProps> = ({
	value,
	options,
	onChange,
	customOptionLabel = "Others – Enter custom terms",
	customInputLabel = "Enter custom terms",
	customInputPlaceholder = "Typing text here",
	...props
}) => {
	const [open, setOpen] = useState(false);
	const [customMode, setCustomMode] = useState(false);
	const [draft, setDraft] = useState("");
	const isCustomValue = !!value && !options.some((option) => option.value === value);
	const displayedOptions = isCustomValue ? [{ value, label: value }, ...options] : options;

	const openCustomEntry = () => {
		setDraft(isCustomValue ? value || "" : "");
		setCustomMode(true);
		setOpen(true);
	};

	const completeCustomEntry = () => {
		const nextValue = draft.trim();
		if (!nextValue) return;
		onChange(nextValue);
		setCustomMode(false);
		setOpen(false);
	};

	return (
		<AppSelect
			{...props}
			value={value}
			options={displayedOptions}
			open={open}
			onOpenChange={(nextOpen) => {
				setOpen(nextOpen);
				if (!nextOpen) setCustomMode(false);
			}}
			onChange={(nextValue) => {
				onChange(nextValue);
				setOpen(false);
			}}
			popupMatchSelectWidth
			dropdownRender={(menu) =>
				customMode ? (
					<CustomPanel onMouseDown={(event) => event.stopPropagation()}>
						<CustomLabel>{customInputLabel}</CustomLabel>
						<CustomInput
							autoFocus
							value={draft}
							placeholder={customInputPlaceholder}
							onChange={(event) => setDraft(event.target.value)}
							onPressEnter={completeCustomEntry}
						/>
						<CustomActions>
							<CustomAction
								type="button"
								onMouseDown={keepCustomPanelOpen}
								onClick={() => setCustomMode(false)}
							>
								<LeftOutlined /> Back
							</CustomAction>
							<CustomAction
								type="button"
								disabled={!draft.trim()}
								onMouseDown={keepCustomPanelOpen}
								onClick={completeCustomEntry}
							>
								<CheckOutlined /> Done
							</CustomAction>
						</CustomActions>
					</CustomPanel>
				) : (
					<>
						{menu}
						<CustomOption
							type="button"
							onMouseDown={(event) => {
								event.preventDefault();
								event.stopPropagation();
								openCustomEntry();
							}}
						>
							<span>{customOptionLabel}</span>
							<RightOutlined />
						</CustomOption>
					</>
				)
			}
		/>
	);
};
